<template>
  <div class="user-form">
    <div class="row justify-content-center">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col">
                <h5 class="mb-0">
                  {{ isEditMode ? $t('users.editUser') : $t('users.addUser') }}
                </h5>
              </div>
            </div>
          </div>
          
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger" role="alert">
              <i class="ti ti-alert-circle me-2"></i>
              {{ error }}
            </div>
            
            <!-- User Form -->
            <form v-else @submit.prevent="saveUser">
              <div class="row">
                <!-- Username (Email) -->
                <div class="col-md-6 mb-3">
                  <label for="userName" class="form-label form-label-lg">
                    {{ $t('users.fields.username') }} (Email)
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    class="form-control"
                    id="userName"
                    v-model="form.userName"
                    :placeholder="$t('users.placeholders.email')"
                    required
                  />
                </div>
                
                <!-- Display Name -->
                <div class="col-md-6 mb-3">
                  <label for="displayUserName" class="form-label form-label-lg">
                    {{ $t('users.fields.displayName') }}
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="displayUserName"
                    v-model="form.displayUserName"
                    :placeholder="$t('users.placeholders.displayName')"
                  />
                </div>
                
                <!-- First Name -->
                <div class="col-md-6 mb-3">
                  <label for="name" class="form-label form-label-lg">
                    {{ $t('users.fields.firstName') }}
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="name"
                    v-model="form.name"
                    :placeholder="$t('users.placeholders.firstName')"
                  />
                </div>
                
                <!-- Last Name -->
                <div class="col-md-6 mb-3">
                  <label for="surname" class="form-label form-label-lg">
                    {{ $t('users.fields.lastName') }}
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="surname"
                    v-model="form.surname"
                    :placeholder="$t('users.placeholders.lastName')"
                  />
                </div>
                
                <!-- Phone Number -->
                <div class="col-md-6 mb-3">
                  <label for="phoneNumber" class="form-label form-label-lg">
                    {{ $t('users.fields.phone') }}
                  </label>
                  <input
                    type="tel"
                    class="form-control"
                    id="phoneNumber"
                    v-model="form.phoneNumber"
                    :placeholder="$t('users.placeholders.phone')"
                  />
                </div>
                
                <!-- Send Password Setup Email (Edit Mode Only) -->
                <div v-if="isEditMode" class="col-12 mb-3">
                  <div class="alert alert-info">
                    <h6 class="alert-heading">{{ $t('users.passwordSetup.title') }}</h6>
                    <p class="mb-2">{{ $t('users.passwordSetup.description') }}</p>
                    <button 
                      type="button" 
                      class="btn btn-outline-primary btn-sm"
                      @click="sendPasswordSetupEmail"
                      :disabled="sendingEmail"
                    >
                      <span v-if="sendingEmail" class="spinner-border spinner-border-sm me-2" role="status"></span>
                      <i v-else class="ti ti-mail me-2"></i>
                      {{ $t('users.passwordSetup.sendEmail') }}
                    </button>
                  </div>
                </div>
                
                <!-- Active Status (only for edit mode) -->
                <div v-if="isEditMode" class="col-12 mb-3">
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="isActive"
                      v-model="form.isActive"
                    />
                    <label class="form-check-label" for="isActive">
                      {{ $t('users.fields.active') }}
                    </label>
                  </div>
                </div>
              </div>
              
              <!-- Form Actions -->
              <div class="row">
                <div class="col-12">
                  <div class="d-flex justify-content-end gap-2">
                    
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary"
                      @click="$router.push('/users')"
                    >
                      {{ $t('common.actions.cancel') }}
                    </button>
                    <button 
                      type="submit" 
                      class="btn btn-primary"
                      :disabled="saving"
                    >
                      <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                      {{ isEditMode ? $t('common.actions.update') : $t('common.actions.create') }}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import userService from '@/services/userService';
import Swal from 'sweetalert2';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const loading = ref(false);
const saving = ref(false);
const error = ref('');

const isEditMode = computed(() => !!route.params.id);

const form = ref({
  userName: '',
  displayUserName: '',
  name: '',
  surname: '',
  phoneNumber: '',
  isActive: true
});

const sendingEmail = ref(false);

const loadUser = async () => {
  if (!isEditMode.value) return;
  
  loading.value = true;
  error.value = '';
  
  try {
    const userData = await userService.getTenantUserById(route.params.id);
    // Map email back to userName for editing
    if (userData.email && !userData.userName) {
      userData.userName = userData.email;
    }
    Object.assign(form.value, userData);
  } catch (err) {
    console.error('Error loading user:', err);
    error.value = t('users.errors.loadFailed');
  } finally {
    loading.value = false;
  }
};

const validateForm = () => {
  if (!form.value.userName.trim()) {
    Swal.fire({
      title: t('common.actions.error'),
      text: t('users.validation.usernameRequired'),
      icon: 'error'
    });
    return false;
  }
  
  // Validate email format
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(form.value.userName.trim())) {
    Swal.fire({
      title: t('common.actions.error'),
      text: t('users.validation.emailInvalid'),
      icon: 'error'
    });
    return false;
  }
  
  return true;
};

const saveUser = async () => {
  if (!validateForm()) return;
  
  saving.value = true;
  
  try {
    const userData = { ...form.value };
    
    // Set email to same value as userName
    userData.email = userData.userName;
    
    if (isEditMode.value) {
      await userService.updateUser(route.params.id, userData);
      Swal.fire({
        title: t('common.actions.success'),
        text: t('users.updateSuccess'),
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      await userService.createUser(userData);
      Swal.fire({
        title: t('common.actions.success'),
        text: t('users.createSuccess'),
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    }
    
    router.push('/users');
  } catch (err) {
    console.error('Error saving user:', err);
    
    // Get specific error message from API response
    let errorMessage = isEditMode.value ? t('users.errors.updateFailed') : t('users.errors.createFailed');
    
    if (err.response && err.response.data && err.response.data.error) {
      const apiError = err.response.data.error;
      
      // Check for validation errors first (most specific)
      if (apiError.validationErrors && apiError.validationErrors.length > 0) {
        // Combine all validation error messages
        errorMessage = apiError.validationErrors
          .map(error => error.message)
          .join('\n');
      } else if (apiError.details) {
        // Use details if available (contains formatted validation errors)
        errorMessage = apiError.details.replace(/\r\n/g, '\n').trim();
      } else if (apiError.message) {
        // Fall back to generic message
        errorMessage = apiError.message;
      }
    }
    
    Swal.fire({
      title: t('common.actions.error'),
      html: errorMessage.replace(/\n/g, '<br>'),
      icon: 'error'
    });
  } finally {
    saving.value = false;
  }
};

const sendPasswordSetupEmail = async () => {
  if (!form.value.userName.trim()) {
    Swal.fire({
      title: t('common.actions.error'),
      text: t('users.validation.usernameRequired'),
      icon: 'error'
    });
    return;
  }

  sendingEmail.value = true;
  
  try {
    await userService.sendPasswordSetupEmail(form.value.userName);
    Swal.fire({
      title: t('common.actions.success'),
      text: t('users.passwordSetup.emailSent'),
      icon: 'success',
      timer: 3000,
      showConfirmButton: false
    });
  } catch (err) {
    console.error('Error sending password setup email:', err);
    
    let errorMessage = t('users.passwordSetup.emailError');
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.response?.data?.error?.message) {
      errorMessage = err.response.data.error.message;
    }
    
    Swal.fire({
      title: t('common.actions.error'),
      text: errorMessage,
      icon: 'error'
    });
  } finally {
    sendingEmail.value = false;
  }
};

onMounted(() => {
  loadUser();
});
</script>

<style scoped>
.user-form {
  padding: 0;
}

.form-label { font-weight: 500; margin-bottom: 0.5rem; }
.form-label-lg { font-size: 0.95rem; font-weight: 600; }

.form-control:focus {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
}

.form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.text-danger {
  color: #dc3545 !important;
}


</style> 